var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CpslButton, CpslIcon, CpslInput, CpslSelect, CpslSelectItem, CpslSpinner, CpslText, } from '@usecapsule/react-components';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import countryCodes from './countryCodes.js';
import { useCapsuleStore, useModalStore, useUserInfoStore } from '../../stores/index.js';
import { EMAIL_REGEX, MOBILE_SIZE } from '../../constants/constants.js';
import { useDropdownPosition } from './hooks/useDropdownPosition.js';
import { ModalStep } from '../../utils/steps.js';
import { defaultPhoneMask, phoneMasks } from './phoneMasks.js';
import { AuthMethod } from '@usecapsule/web-sdk';
const DEFAULT_COUNTRY = { label: 'United States', value: '+1', selectedLabel: 'US', icon: 'US' };
export const AuthInput = ({ disableEmailLogin, disablePhoneLogin }) => {
    var _a;
    const inputRef = useRef(null);
    const { dropdownMaxHeight, dropdownWidth } = useDropdownPosition(inputRef);
    const capsule = useCapsuleStore(state => state.capsule);
    const setCountryCode = useUserInfoStore(state => state.setCountryCode);
    const setIdentifier = useUserInfoStore(state => state.setIdentifier);
    const identifier = useUserInfoStore(state => state.identifier);
    const identifierType = useUserInfoStore(state => state.identifierType);
    const countryCode = useUserInfoStore(state => state.countryCode);
    const setIdentifierType = useUserInfoStore(state => state.setIdentifierType);
    const setFlow = useModalStore(state => state.setFlow);
    const setStep = useModalStore(state => state.setStep);
    const setSupportedAuthMethods = useModalStore(state => state.setSupportedAuthMethods);
    const setBiometricLocationHints = useModalStore(state => state.setBiometricLocationHints);
    const [matchedCountryCode, setMatchedCountryCode] = useState(DEFAULT_COUNTRY);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const isEmail = identifierType === 'email';
    const isPhone = identifierType === 'phone';
    const isUnknown = !identifierType;
    const filteredCountryCodes = search
        ? countryCodes.filter(cc => cc.selectedLabel.toLowerCase().includes(search.toLowerCase()) ||
            cc.label.toLowerCase().includes(search.toLowerCase()) ||
            cc.value.toLowerCase().includes(search.toLowerCase()))
        : countryCodes;
    const handleSearchInput = (ev) => {
        setSearch(ev.detail);
    };
    const handleIdentifierInput = (ev) => {
        const newIdentifier = ev.detail.value;
        let isNewPhone = false, isNewEmail = false;
        if (!disablePhoneLogin) {
            const countryCodeInputMatch = countryCodes.find(cc => cc.value === newIdentifier);
            if (countryCodeInputMatch) {
                setCountryCode(countryCodeInputMatch.value);
                setMatchedCountryCode(countryCodeInputMatch);
                setIdentifierType('phone');
                setIdentifier('');
                return;
            }
            isNewPhone = !isEmail && isPhone ? /\d+$/.test(newIdentifier) : /\d\d\d+$/.test(newIdentifier);
        }
        if (!disableEmailLogin) {
            isNewEmail = /\D.*$/.test(newIdentifier);
        }
        setIdentifierType(isNewEmail ? 'email' : isNewPhone ? 'phone' : undefined);
        setIdentifier(newIdentifier);
    };
    const handleCountryCodeInput = (ev) => {
        const matchedCountryCode = countryCodes.find(code => code.selectedLabel === ev.detail);
        setCountryCode(matchedCountryCode.value);
        setMatchedCountryCode(matchedCountryCode);
    };
    const login = () => __awaiter(void 0, void 0, void 0, function* () {
        setError('');
        if (isUnknown) {
            setError('Please enter a valid email or phone number!');
            return;
        }
        if (isEmail) {
            if (!EMAIL_REGEX.test(identifier)) {
                setError('Please enter a valid email!');
                return;
            }
            // Logout to ensure cleared Capsule state but preserve pregen wallets
            yield capsule.logout(true);
            const userExists = yield capsule.checkIfUserExists(identifier);
            if (userExists) {
                const supportedAuthMethods = yield capsule.initiateUserLoginV2(identifier, 'email');
                const biometricLocationHints = supportedAuthMethods.has(AuthMethod.PASSKEY)
                    ? yield capsule.getUserBiometricLocationHints()
                    : [];
                setFlow('login');
                setStep(ModalStep.BIOMETRIC_LOGIN);
                setSupportedAuthMethods(supportedAuthMethods);
                setBiometricLocationHints(biometricLocationHints);
                return;
            }
            yield capsule.createUser(identifier);
            setFlow('signUp');
            setStep(ModalStep.VERIFICATIONS);
            return;
        }
        if (isPhone) {
            // Logout to ensure cleared Capsule state but preserve pregen wallets
            yield capsule.logout(true);
            let userExists = false;
            try {
                userExists = yield capsule.checkIfUserExistsByPhone(identifier, countryCode);
            }
            catch (error) {
                setError('Please enter a valid phone number!');
                return;
            }
            if (userExists) {
                const supportedAuthMethods = yield capsule.initiateUserLoginV2(identifier, 'phone', countryCode);
                const biometricLocationHints = supportedAuthMethods.has(AuthMethod.PASSKEY)
                    ? yield capsule.getUserBiometricLocationHints()
                    : [];
                setFlow('login');
                setStep(ModalStep.BIOMETRIC_LOGIN);
                setSupportedAuthMethods(supportedAuthMethods);
                setBiometricLocationHints(biometricLocationHints);
                return;
            }
            yield capsule.createUserByPhone(identifier, countryCode);
            setFlow('signUp');
            setStep(ModalStep.VERIFICATIONS);
            return;
        }
    });
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoggingIn(true);
        yield login();
        setIsLoggingIn(false);
    });
    if (disableEmailLogin && disablePhoneLogin) {
        return null;
    }
    return (_jsx("form", { onSubmit: (e) => __awaiter(void 0, void 0, void 0, function* () {
            e.preventDefault();
            yield handleSubmit();
        }), children: _jsxs(StyledInput, { ref: inputRef, id: "authInput", placeholder: isEmail || disablePhoneLogin
                ? 'Enter email'
                : isPhone || disableEmailLogin
                    ? 'Enter phone'
                    : 'Enter email or phone', onCpslInput: handleIdentifierInput, value: identifier, errorText: error, autofocus: true, inputMode: "email", onKeyDown: (e) => __awaiter(void 0, void 0, void 0, function* () { return e.key === 'Enter' && handleSubmit(); }), contrastText: true, isPhone: isPhone, mask: identifierType === 'phone' ? ((_a = phoneMasks[matchedCountryCode.selectedLabel]) !== null && _a !== void 0 ? _a : defaultPhoneMask) : undefined, enterkeyhint: "go", noAutoDisable: true, disabled: isLoggingIn, children: [_jsxs(IconContainer, { slot: "start", children: [!disableEmailLogin && (isUnknown || isEmail) && _jsx(CpslIcon, { icon: "mail" }), !disablePhoneLogin && isUnknown && _jsx(CpslIcon, { icon: "phone" }), isPhone && (_jsxs(CountryCodeSelect, { selectedValue: matchedCountryCode.selectedLabel, onCpslSelectValueChange: handleCountryCodeInput, showFormattedSelectedItem: true, autoWidth: true, dropdownMaxHeight: dropdownMaxHeight, anchorElId: "authInput", "$width": dropdownWidth, showSearch: true, searchPlaceholder: "Search Countries", onCpslSearchChange: handleSearchInput, children: [matchedCountryCode && (_jsxs(SelectedItem, { slot: "selected-item", children: [_jsx(CpslText, { children: matchedCountryCode.selectedLabel }), _jsx(CpslText, { children: matchedCountryCode.value })] })), filteredCountryCodes.map(cc => (_jsxs(StyledSelectItem, { slot: "items", value: cc.selectedLabel, children: [_jsx(CpslText, { children: cc.label }), _jsx(CpslText, { children: cc.value })] }, cc.selectedLabel)))] }))] }), identifier && (_jsx(CpslButton, { slot: "end", size: "small", fullWidth: true, disabled: isLoggingIn, onClick: handleSubmit, children: isLoggingIn ? _jsx(CpslSpinner, { size: 16 }) : _jsx(CpslIcon, { icon: "arrowNarrow" }) }))] }, 'email') }));
};
const IconContainer = styled.div `
  height: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
  border-right: 1px solid var(--cpsl-color-background-16);

  & cpsl-icon {
    --icon-color: var(--cpsl-color-contrast);
  }
`;
const CountryCodeSelect = styled(CpslSelect) `
  --container-height: 100%;
  --container-padding-start: 0px;
  --container-padding-end: 0px;
  --container-border-width: 0px;
  --container-gap: 4px;
  --container-background-color: transparent;
  --container-box-shadow: none;

  &::part(dropdown) {
    width: ${({ $width }) => `${$width - 2}px`};
  }

  &::part(popover) {
    @media (max-width: ${MOBILE_SIZE}px) {
      top: unset !important;
      bottom: 16px;
    }

    cpsl-auth-modal.force-mobile-media & {
      top: unset !important;
      bottom: 16px;
    }
  }
`;
const StyledSelectItem = styled(CpslSelectItem) `
  &::part(inner-container) {
    justify-content: space-between;
  }
`;
const SelectedItem = styled.div `
  display: flex;
  gap: 4px;
  align-items: center;
`;
const StyledInput = styled(CpslInput) `
  --container-background-color: var(--cpsl-color-background-8);
  --input-background-color: var(--cpsl-color-background-8);
  --container-padding-end: 8px;
`;
